// =============================================================================
// domain/loggerManifest.ts — what the desktop-logger download card reads.
// -----------------------------------------------------------------------------
// The published builds are listed in a single `manifest.json` in a PUBLIC
// storage bucket. This file is the pure half: what a valid entry looks like, and
// how it reads on screen.
//
// ── ⚠ THE MANIFEST IS WRITTEN BY A RELEASE PROCESS, NOT BY THIS APP ──
//
// So every field is treated as untrusted. A half-published entry — a version
// with no object, a size that arrived as a string — must degrade to "coming
// soon" rather than render a download button that 404s. A dead download button
// is worse than an absent one: it sends someone to install a file they never
// received.
// =============================================================================

export interface LoggerBuild {
  /** Display version, e.g. `2026.07.11`. */
  version: string;
  /** Bytes. */
  size: number;
  /** Path inside the bucket. ⚠ Ends in the real filename — see `downloadUrl`. */
  object: string;
}

export interface LoggerPlatform {
  key: string;
  /** The heading, as the old client writes it. */
  label: string;
}

/** Both platforms, in the old client's order. */
export const LOGGER_PLATFORMS: readonly LoggerPlatform[] = [
  { key: 'atas', label: 'ATAS — JTF Trade Logger' },
  { key: 'sierra', label: 'Sierra Chart — JTF Trade Sync' },
];

/**
 * A usable build, or `null`.
 *
 * ⚠ ALL THREE FIELDS ARE REQUIRED. `object` in particular: without it the
 * download URL points at the bucket root, which is a button that cannot work.
 * `size` is allowed to be a numeric string, because a hand-edited manifest is a
 * real possibility and a quoted number is the most likely slip.
 */
export function parseBuild(raw: unknown): LoggerBuild | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;

  const object = typeof r['object'] === 'string' ? r['object'].trim() : '';
  if (!object) return null;

  const version = r['version'] === undefined || r['version'] === null
    ? '' : String(r['version']).trim();
  if (!version) return null;

  const size = Number(r['size']);
  return {
    version,
    // ⚠ A missing or unreadable size is ZERO, not a reason to reject the build.
    // The file is real; only its label is unknown, and "0 KB" is visibly odd in
    // a way that a hidden download is not.
    size: Number.isFinite(size) && size > 0 ? size : 0,
    object,
  };
}

export type LoggerManifest = Record<string, LoggerBuild | null>;

export function parseManifest(raw: unknown): LoggerManifest {
  const out: LoggerManifest = {};
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  for (const p of LOGGER_PLATFORMS) out[p.key] = parseBuild(r[p.key]);
  return out;
}

/**
 * The download link.
 *
 * ⚠ `?v=` IS A CACHE-BUSTER, not a parameter the server reads. Storage serves
 * these with a long cache lifetime, so without it a user who downloaded last
 * month's build gets it again from disk and reports the new version as broken.
 *
 * ⚠ THE `download` ATTRIBUTE ON THE ANCHOR IS IGNORED — it is cross-origin, and
 * browsers drop the attribute for a different origin. The filename comes from
 * the OBJECT PATH instead, which is why the manifest stores a path ending in the
 * real `.dll` name rather than an opaque id. The old client's comment says the
 * same; it is not an accident to be tidied.
 */
export function downloadUrl(base: string, build: LoggerBuild): string {
  const root = String(base ?? '').replace(/\/+$/, '');
  return `${root}/${build.object}?v=${encodeURIComponent(build.version)}`;
}

/** The public bucket the release process publishes into. */
export const loggerBucketBase = (supabaseUrl: string): string =>
  `${String(supabaseUrl ?? '').replace(/\/+$/, '')}/storage/v1/object/public/loggers`;

/**
 * `53 KB`.
 *
 * ⚠ KILOBYTES, ROUNDED, matching the old client — the two clients show the same
 * build and a different unit would read as a different file. The Sierra build is
 * ~972 KB, so nothing here reaches megabytes yet; when it does, this is the one
 * place to change.
 */
export const formatSize = (bytes: number): string =>
  `${Math.round((Number(bytes) || 0) / 1024)} KB`;
